import React, { useState } from 'react';
import { Settings, ShoppingCart, User, LogIn, Package, Menu, X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const MainNavigation = ({ onNavigate, currentPage = 'home', cartCount = 0 }) => {
  const { user, isAuthenticated, loading, loginWithGoogle, testLogin } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navigateToPage = (page) => {
    setMobileMenuOpen(false); // Close mobile menu on navigation
    if (onNavigate) {
      onNavigate(page);
    }
  };

  const scrollToContact = () => {
    setMobileMenuOpen(false); // Close mobile menu
    if (currentPage === 'home') {
      // If on home page, scroll to contact section
      const contactSection = document.querySelector('#contact');
      if (contactSection) {
        contactSection.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      // If on other page, go to home then scroll to contact
      navigateToPage('home');
      setTimeout(() => {
        const contactSection = document.querySelector('#contact');
        if (contactSection) {
          contactSection.scrollIntoView({ behavior: 'smooth' });
        }
      }, 500);
    }
  };

  return (
    <header className="bg-white/95 backdrop-blur-sm border-b border-slate-100 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <div className="flex items-center">
          {/* Logo with minimum width and spacing */}
          <button 
            onClick={() => navigateToPage('home')}
            className="flex items-center flex-shrink-0"
          >
            <img 
              src="/logo-nav.png" 
              alt="Create 3D Works Logo" 
              className="h-10 sm:h-12 w-auto object-contain"
              style={{ minWidth: '120px' }}
            />
          </button>

          {/* Spacer matching menu item spacing */}
          <div className="w-8 flex-shrink-0 hidden lg:block"></div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex space-x-8 items-center flex-1">
            <button 
              onClick={() => navigateToPage("services")} 
              className={`font-medium transition-colors ${
                currentPage === 'services' 
                  ? 'text-[#0007b5]' 
                  : 'text-slate-600 hover:text-[#0007b5]'
              }`}
            >
              Services
            </button>
            <button 
              onClick={() => navigateToPage("products")} 
              className={`font-medium transition-colors ${
                currentPage === 'products' 
                  ? 'text-[#0007b5]' 
                  : 'text-slate-600 hover:text-[#0007b5]'
              }`}
            >
              Shop
            </button>
            <button 
              onClick={() => navigateToPage("marketplace")} 
              className={`font-medium transition-colors ${
                currentPage === 'marketplace' 
                  ? 'text-[#0007b5]' 
                  : 'text-slate-600 hover:text-[#0007b5]'
              }`}
            >
              Designs
            </button>
            <button 
              onClick={() => navigateToPage("about")} 
              className={`font-medium transition-colors ${
                currentPage === 'about' 
                  ? 'text-[#0007b5]' 
                  : 'text-slate-600 hover:text-[#0007b5]'
              }`}
            >
              About
            </button>
            <button 
              onClick={() => navigateToPage("contact")}
              className={`font-medium transition-colors ${
                currentPage === 'contact' 
                  ? 'text-[#0007b5]' 
                  : 'text-slate-600 hover:text-[#0007b5]'
              }`}
            >
              Contact
            </button>
            <button 
              onClick={() => navigateToPage("home")} 
              className={`font-medium transition-colors ${
                currentPage === 'home' 
                  ? 'text-[#0007b5]' 
                  : 'text-slate-600 hover:text-[#0007b5]'
              }`}
            >
              Home
            </button>
          </nav>

          {/* Right-aligned Cart and Auth buttons for Desktop */}
          <div className="hidden lg:flex items-center space-x-4 ml-auto">
            {/* Shopping Cart */}
            <button 
              onClick={() => navigateToPage("cart")}
              className={`relative flex items-center space-x-2 font-medium transition-colors ${
                currentPage === 'cart' 
                  ? 'text-[#0007b5]' 
                  : 'text-slate-600 hover:text-[#0007b5]'
              }`}
            >
              <ShoppingCart className="w-5 h-5" />
              {cartCount > 0 && (
                <span className="absolute -top-2 -right-2 bg-[#0007b5] text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                  {cartCount}
                </span>
              )}
              <span className="hidden sm:inline">Cart</span>
            </button>
            
            {/* Firebase Authentication */}
            {loading ? (
              <div className="flex items-center space-x-2 text-slate-600 font-medium">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#0007b5]"></div>
                <span>Loading...</span>
              </div>
            ) : isAuthenticated ? (
              <div className="flex items-center space-x-4">
                <button 
                  onClick={() => navigateToPage("profile")}
                  className={`flex items-center space-x-2 font-medium transition-colors ${
                    currentPage === 'profile' 
                      ? 'text-[#0007b5]' 
                      : 'text-slate-600 hover:text-[#0007b5]'
                  }`}
                >
                  <User className="w-4 h-4" />
                  <span className="hidden sm:inline">{user?.name || 'Profile'}</span>
                </button>
              </div>
            ) : (
              <button
                onClick={loginWithGoogle}
                className="bg-[#0007b5] hover:bg-[#000690] text-white px-6 py-2 rounded-lg font-medium transition-colors flex items-center space-x-2"
              >
                <LogIn className="w-4 h-4" />
                <span>Sign In</span>
              </button>
            )}
          </div>

          {/* Mobile menu button - Right side actions */}
          <div className="flex lg:hidden items-center gap-3 ml-auto">
            {/* Cart icon for mobile */}
            <button 
              onClick={() => navigateToPage('cart')}
              className="relative p-2 hover:bg-slate-100 rounded-lg transition-colors"
              aria-label="Shopping cart"
            >
              <ShoppingCart className="w-6 h-6 text-slate-600" />
              {cartCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-[#0007b5] text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-medium">
                  {cartCount}
                </span>
              )}
            </button>
            
            {/* Hamburger button - Simple three lines */}
            <button 
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? (
                <X className="w-6 h-6 text-slate-600" />
              ) : (
                <div className="space-y-1.5">
                  <div className="w-6 h-0.5 bg-slate-600"></div>
                  <div className="w-6 h-0.5 bg-slate-600"></div>
                  <div className="w-6 h-0.5 bg-slate-600"></div>
                </div>
              )}
            </button>
          </div>
        </div>

        {/* Mobile Menu Panel */}
        {mobileMenuOpen && (
          <div className="lg:hidden mt-4 pb-4 border-t border-slate-100 pt-4">
            <nav className="flex flex-col space-y-4">
              <button 
                onClick={() => navigateToPage("services")} 
                className={`text-left font-medium transition-colors py-2 ${
                  currentPage === 'services' 
                    ? 'text-[#0007b5]' 
                    : 'text-slate-600 hover:text-[#0007b5]'
                }`}
              >
                Services
              </button>
              <button 
                onClick={() => navigateToPage("products")} 
                className={`text-left font-medium transition-colors py-2 ${
                  currentPage === 'products' 
                    ? 'text-[#0007b5]' 
                    : 'text-slate-600 hover:text-[#0007b5]'
                }`}
              >
                Shop
              </button>
              <button 
                onClick={() => navigateToPage("marketplace")} 
                className={`text-left font-medium transition-colors py-2 ${
                  currentPage === 'marketplace' 
                    ? 'text-[#0007b5]' 
                    : 'text-slate-600 hover:text-[#0007b5]'
                }`}
              >
                Designs
              </button>
              <button 
                onClick={() => navigateToPage("about")} 
                className={`text-left font-medium transition-colors py-2 ${
                  currentPage === 'about' 
                    ? 'text-[#0007b5]' 
                    : 'text-slate-600 hover:text-[#0007b5]'
                }`}
              >
                About
              </button>
              <button 
                onClick={() => navigateToPage("contact")} 
                className={`text-left font-medium transition-colors py-2 ${
                  currentPage === 'contact' 
                    ? 'text-[#0007b5]' 
                    : 'text-slate-600 hover:text-[#0007b5]'
                }`}
              >
                Contact
              </button>
              <button 
                onClick={() => navigateToPage("home")} 
                className={`text-left font-medium transition-colors py-2 ${
                  currentPage === 'home' 
                    ? 'text-[#0007b5]' 
                    : 'text-slate-600 hover:text-[#0007b5]'
                }`}
              >
                Home
              </button>

              {/* Divider */}
              <div className="border-t border-slate-200 my-2"></div>

              {/* Auth buttons in mobile menu */}
              {isAuthenticated && user ? (
                <>
                  {user.is_designer && (
                    <button 
                      onClick={() => navigateToPage('designer-dashboard')}
                      className="text-left flex items-center space-x-2 py-2 text-slate-600 hover:text-[#0007b5] transition-colors"
                    >
                      <Package className="w-4 h-4" />
                      <span>My Designs</span>
                    </button>
                  )}
                  <button 
                    onClick={() => navigateToPage('profile')}
                    className="text-left flex items-center space-x-2 py-2 text-slate-600 hover:text-[#0007b5] transition-colors"
                  >
                    <User className="w-4 h-4" />
                    <span>{user.name || user.email}</span>
                  </button>
                </>
              ) : (
                <button 
                  onClick={() => loginWithGoogle()}
                  className="bg-[#0007b5] hover:bg-[#000690] text-white px-6 py-3 rounded-lg font-medium transition-colors flex items-center justify-center space-x-2 w-full"
                >
                  <LogIn className="w-4 h-4" />
                  <span>Sign In</span>
                </button>
              )}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default MainNavigation;